import React, { useMemo, useState } from 'react';
import { Alert, Button, Card, Col, DatePicker, Row, Space, Spin, Statistic, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined, PrinterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  getOperationalProfit,
  type OperationalProfitReport,
  type OperationalProfitTotals,
  type UncostedDeliveryLine,
} from '@/services/financialReports.service';
import { PrintPreviewChrome } from '@/components/print/PrintPreviewChrome';
import { downloadXlsx } from '@/reports/utils/exportTable';
import { formatDate, formatMoney, formatNumber, formatPercent, PRINT_PLAIN_AMOUNT_PROFILE } from '@/utils/format';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

function amt(v: number | null | undefined): string {
  return formatMoney(v ?? 0, PRINT_PLAIN_AMOUNT_PROFILE);
}

function pctText(v: number | null | undefined): string {
  return v == null ? '—' : formatPercent(v);
}

type RowKind = 'header' | 'line' | 'memo' | 'result' | 'margin' | 'count';

type MetricKey = keyof OperationalProfitTotals;

type StatementRow = {
  key: string;
  label: string;
  kind: RowKind;
  metric?: MetricKey;
};

function getStatementRows(t: TFunction): StatementRow[] {
  return [
    { key: 'h-sales', label: t('salesFromDeliveries'), kind: 'header' },
    { key: 'delivered', label: t('valueDelivered'), kind: 'line', metric: 'delivered_value' },
    { key: 'invoiced', label: t('invoicedMemo'), kind: 'memo', metric: 'invoiced_value' },
    { key: 'h-cost', label: t('costOfWhatDelivered'), kind: 'header' },
    { key: 'est', label: t('estimatedCostCustomerOrder'), kind: 'line', metric: 'estimated_cost' },
    { key: 'stock', label: t('stockIssueCost'), kind: 'line', metric: 'stock_cost' },
    { key: 'cost', label: t('totalCostOfDelivered'), kind: 'line', metric: 'delivered_cost' },
    { key: 'gp', label: t('grossProfitOperationsUpper'), kind: 'result', metric: 'gross_profit' },
    { key: 'gpm', label: t('grossMarginPct'), kind: 'margin', metric: 'gross_margin_pct' },
    { key: 'oh', label: t('overheadsFromFinance'), kind: 'line', metric: 'overhead' },
    { key: 'oi', label: t('otherIncomeFromFinance'), kind: 'line', metric: 'other_income' },
    { key: 'np', label: t('indicativeNetProfitUpper'), kind: 'result', metric: 'net_profit' },
    { key: 'npm', label: t('netMarginPct'), kind: 'margin', metric: 'net_margin_pct' },
    { key: 'fnp', label: t('financeNetProfitRef'), kind: 'memo', metric: 'finance_net_profit' },
    { key: 'h-spend', label: t('actualSpendCashView'), kind: 'header' },
    { key: 'grn', label: t('goodsReceivedGrn'), kind: 'line', metric: 'goods_received' },
    { key: 'npo', label: t('supplierInvoicesWithoutPo'), kind: 'line', metric: 'non_po_invoices' },
    { key: 'spend', label: t('totalActualSpend'), kind: 'result', metric: 'actual_spend' },
    { key: 'h-gap', label: t('dataGaps'), kind: 'header' },
    { key: 'ucl', label: t('deliveredLinesWithoutCost'), kind: 'count', metric: 'uncosted_lines' },
    { key: 'ucv', label: t('valueOfThoseLines'), kind: 'memo', metric: 'uncosted_value' },
  ];
}

function metricValues(report: OperationalProfitReport, metric: MetricKey): Array<number | null> {
  return report.rows.map((r) => r[metric] as number | null);
}

const OperationalProfitPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const [searchParams, setSearchParams] = useSearchParams();
  const fromMonth = searchParams.get('from') || undefined;
  const toMonth = searchParams.get('to') || undefined;
  const [printing, setPrinting] = useState(false);

  const query = useQuery({
    queryKey: ['fin-operational-profit', fromMonth, toMonth],
    queryFn: () => getOperationalProfit({ from_month: fromMonth, to_month: toMonth }),
  });
  const report = query.data;
  const errorMessage = (query.error as any)?.response?.data?.message || (query.error as Error | null)?.message;

  const pickerFrom = fromMonth ?? report?.from_month;
  const pickerTo = toMonth ?? report?.to_month;

  const exportExcel = () => {
    if (!report) return;
    const rows = getStatementRows(t).filter((r) => r.kind !== 'header' && r.metric).map((r) => {
      const out: Record<string, unknown> = { label: r.label, total: report.totals[r.metric!] ?? '' };
      report.rows.forEach((m) => {
        out[m.month] = m[r.metric!] ?? '';
      });
      return out;
    });
    const uncosted = report.uncosted.map((u) => ({
      label: [u.delivery_note_number, u.sales_order_number, u.item_code, u.description].filter(Boolean).join(' · '),
      total: u.value,
    }));
    const columns = [
      { key: 'label', dataIndex: 'label', title: t('line') },
      ...report.months.map((m) => ({ key: m.key, dataIndex: m.key, title: m.label })),
      { key: 'total', dataIndex: 'total', title: t('total') },
    ];
    void downloadXlsx(
      `operational-profit-${report.from_month}-${report.to_month}`,
      columns,
      uncosted.length ? [...rows, { label: '' }, { label: t('deliveredLinesWithoutCost') }, ...uncosted] : rows
    );
  };

  const body = report ? <OperationalStatement report={report} printMode={printing} /> : null;

  if (printing && body) {
    return (
      <PrintPreviewChrome
        title={t('operationalProfitPrintTitle', {
          from: report!.months[0]?.label,
          to: report!.months[report!.months.length - 1]?.label,
        })}
        fileName={`operational-profit-${report!.from_month}-${report!.to_month}`}
        onBack={() => setPrinting(false)}
      >
        <div className="print-doc-a4 print-doc-a4--dimensional-landscape">
          <div style={{ padding: '8mm 10mm', fontSize: 10 }}>{body}</div>
        </div>
      </PrintPreviewChrome>
    );
  }

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Row justify="space-between" align="middle" gutter={[12, 12]}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            {t('operationalProfit')}
          </Title>
          <Text type="secondary">
            {t('operationalProfitHint')}
          </Text>
        </Col>
        <Col>
          <Space>
            <Button icon={<DownloadOutlined />} disabled={!report} onClick={exportExcel}>
              {tc('excel')}
            </Button>
            <Button type="primary" icon={<PrinterOutlined />} disabled={!report} onClick={() => setPrinting(true)}>
              {t('print')}
            </Button>
          </Space>
        </Col>
      </Row>

      <Card size="small">
        <Space wrap size={[16, 8]}>
          <RangePicker
            picker="month"
            allowClear={false}
            format="MMM YYYY"
            value={pickerFrom && pickerTo ? [dayjs(`${pickerFrom}-01`), dayjs(`${pickerTo}-01`)] : null}
            onChange={(v) => {
              if (!v?.[0] || !v?.[1]) return;
              const next = new URLSearchParams(searchParams);
              next.set('from', v[0].format('YYYY-MM'));
              next.set('to', v[1].format('YYYY-MM'));
              setSearchParams(next, { replace: true });
            }}
          />
          <Link to={`/fin/reports/monthly-profit-loss${pickerFrom && pickerTo ? `?from=${pickerFrom}&to=${pickerTo}` : ''}`}>
            {t('compareWithFinanceMonthlyPl')}
          </Link>
        </Space>
      </Card>

      {query.isFetching && !report ? (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <Spin size="large" tip={tc('loading')} />
        </div>
      ) : query.error ? (
        <Alert type="error" showIcon message={t('couldNotLoadOperationalProfit')} description={errorMessage} />
      ) : (
        <Spin spinning={query.isFetching}>{body}</Spin>
      )}
    </div>
  );
};

const OperationalStatement: React.FC<{ report: OperationalProfitReport; printMode: boolean }> = ({ report, printMode }) => {
  const { t } = useTranslation('finance');
  const totals = report.totals;
  const statementRows = useMemo(() => getStatementRows(t), [t]);
  const firstMonth = report.months[0];
  const lastMonth = report.months[report.months.length - 1];

  const chartData = useMemo(
    () =>
      report.rows.map((m, i) => ({
        month: report.months[i]?.label ?? m.month,
        delivered: m.delivered_value,
        invoiced: m.invoiced_value,
        gross_profit: m.gross_profit,
        net_profit: m.net_profit,
        finance_net_profit: m.finance_net_profit,
      })),
    [report]
  );

  const valueCell = (v: number | null, row: StatementRow) => {
    if (row.kind === 'header') return '';
    if (row.kind === 'margin') return pctText(v);
    if (row.kind === 'count') return v ? formatNumber(v, { maximumFractionDigits: 0 }) : '—';
    const value = v ?? 0;
    const neg = value < 0 ? { color: '#cf1322' } : undefined;
    const text = amt(value);
    if (row.kind === 'result') return <strong style={neg}>{text}</strong>;
    if (row.kind === 'memo') return <Text type="secondary">{text}</Text>;
    return <span style={neg}>{text}</span>;
  };

  const labelCell = (v: string, row: StatementRow) => {
    if (row.kind === 'header' || row.kind === 'result') return <strong>{v}</strong>;
    if (row.kind === 'margin' || row.kind === 'memo') return <Text type="secondary" style={{ paddingLeft: 12 }}>{v}</Text>;
    return <span style={{ paddingLeft: 12 }}>{v}</span>;
  };

  const monthWidth = printMode ? undefined : 120;
  const columns: ColumnsType<StatementRow> = [
    {
      title: t('line'),
      dataIndex: 'label',
      fixed: printMode ? undefined : 'left',
      width: printMode ? undefined : 300,
      render: labelCell,
    },
    ...report.months.map((m, i) => ({
      title: m.label,
      key: m.key,
      align: 'right' as const,
      width: monthWidth,
      render: (_: unknown, row: StatementRow) =>
        row.metric ? valueCell(metricValues(report, row.metric)[i], row) : '',
    })),
    {
      title: t('total'),
      key: 'total',
      align: 'right' as const,
      width: printMode ? undefined : 140,
      render: (_: unknown, row: StatementRow) => (row.metric ? valueCell(totals[row.metric] as number | null, row) : ''),
    },
  ];

  const uncostedColumns: ColumnsType<UncostedDeliveryLine> = [
    {
      title: t('deliveryNote'),
      dataIndex: 'delivery_note_number',
      render: (v: string, r) => (printMode ? v : <Link to={`/growth/delivery-notes/${r.delivery_note_id}`}>{v}</Link>),
    },
    { title: t('deliveredOn'), dataIndex: 'delivered_on', width: 110, render: (v: string) => formatDate(v) },
    {
      title: t('customerOrder'),
      dataIndex: 'sales_order_number',
      render: (v: string, r) => (printMode ? v : <Link to={`/growth/orders/${r.sales_order_id}`}>{v}</Link>),
    },
    { title: t('customer'), dataIndex: 'customer_name', render: (v: string | null) => v ?? '—' },
    { title: t('item'), dataIndex: 'item_code', render: (v: string | null) => v ?? '—' },
    { title: t('description'), dataIndex: 'description', ellipsis: true, render: (v: string | null) => v ?? '—' },
    {
      title: t('qty'),
      dataIndex: 'quantity',
      align: 'right',
      width: 90,
      render: (v: number) => formatNumber(v, { maximumFractionDigits: 3 }),
    },
    { title: t('value'), dataIndex: 'value', align: 'right', width: 130, render: (v: number) => amt(v) },
  ];

  const stat = (title: string, value: number, margin?: number | null) => (
    <Statistic
      title={title}
      value={margin !== undefined ? `${amt(value)} (${pctText(margin)})` : amt(value)}
      valueStyle={{ color: value < 0 ? '#cf1322' : undefined, fontWeight: 600 }}
    />
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {printMode ? (
        <div>
          <Title level={4} style={{ margin: 0 }}>
            {t('operationalProfit')}
          </Title>
          <Text>
            {t('periodFromTo', { from: firstMonth?.label, to: lastMonth?.label })} · {report.currency}
            {t('fromDeliveryNotesPurchasing')}
          </Text>
        </div>
      ) : (
        <>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={6}>
              {stat(t('valueDelivered'), totals.delivered_value)}
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('invoicedAmount', { amount: amt(totals.invoiced_value) })}
              </Text>
            </Col>
            <Col xs={24} md={6}>
              {stat(t('costOfDelivered'), totals.delivered_cost)}
            </Col>
            <Col xs={24} md={6}>
              {stat(t('grossProfitOperations'), totals.gross_profit, totals.gross_margin_pct)}
            </Col>
            <Col xs={24} md={6}>
              {stat(t('indicativeNetProfit'), totals.net_profit, totals.net_margin_pct)}
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('financeNetProfitAmount', { amount: amt(totals.finance_net_profit) })}
              </Text>
            </Col>
          </Row>
          {totals.uncosted_lines > 0 ? (
            <Alert
              type="warning"
              showIcon
              message={t('uncostedLinesAlert', {
                count: formatNumber(totals.uncosted_lines, { maximumFractionDigits: 0 }),
                amount: amt(totals.uncosted_value),
              })}
              description={t('uncostedLinesAlertDesc')}
            />
          ) : null}
          <Card size="small" title={t('monthlyTrend')}>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(v: number) => amt(v)} width={100} />
                <Tooltip formatter={(value: number) => amt(value)} />
                <Legend />
                <Bar dataKey="delivered" name={t('valueDelivered')} fill="#91caff" />
                <Bar dataKey="invoiced" name={t('invoiced')} fill="#d9d9d9" />
                <Line type="monotone" dataKey="gross_profit" name={t('grossProfit')} stroke="#1677ff" strokeWidth={2} />
                <Line type="monotone" dataKey="net_profit" name={t('indicativeNetProfit')} stroke="#389e0d" strokeWidth={2} />
                <Line
                  type="monotone"
                  dataKey="finance_net_profit"
                  name={t('financeNetProfit')}
                  stroke="#fa8c16"
                  strokeDasharray="5 5"
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Card>
        </>
      )}

      <Table
        size="small"
        bordered
        pagination={false}
        rowKey="key"
        dataSource={statementRows}
        columns={columns}
        scroll={printMode ? undefined : { x: 300 + report.months.length * 120 + 140 }}
        rowClassName={(r) => (r.kind === 'result' ? 'ant-table-row-selected' : '')}
      />

      {report.uncosted.length ? (
        <Card
          size="small"
          title={report.uncosted_truncated ? t('deliveredLinesWithoutCostLargest') : t('deliveredLinesWithoutCost')}
        >
          <Table
            size="small"
            rowKey={(r) => `${r.delivery_note_id}-${r.item_code ?? ''}-${r.description ?? ''}-${r.value}`}
            dataSource={report.uncosted}
            columns={uncostedColumns}
            pagination={printMode ? false : { pageSize: 20, showSizeChanger: true }}
          />
        </Card>
      ) : null}

      <Text type="secondary" style={{ fontSize: 12 }}>
        {t('operationalProfitFootnote', { currency: report.currency })}
      </Text>
    </div>
  );
};

export default OperationalProfitPage;
